import { $$, el } from './dom.js';
import { Popover, searchField } from './popover.js';
import { EMOJI_CATEGORIES, ALL_EMOJI } from './emoji-data.js';
import { ct } from './i18n.js';

/**
 * Emoji picker.
 *
 * The emoji fields were plain text inputs: you had to know the character or go
 * through the system picker. This panel offers keyword search, category
 * navigation, and accepts the server's custom emojis in `<:name:id>` form -
 * which the bot knows how to render.
 */

const RECENT_KEY = 'bilbi.emoji.recent';
const RECENT_MAX = 24;

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]).slice(0, RECENT_MAX) : [];
  } catch {
    // Storage unavailable (private browsing): we do without, quietly.
    return [];
  }
}

function pushRecent(emoji: string): void {
  try {
    const next = [emoji, ...readRecent().filter((value) => value !== emoji)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignored */
  }
}

export interface EmojiPickerOptions {
  /** Champ recevant l'emoji choisi. */
  input: HTMLInputElement;
  /** Opening button; created automatically when absent. */
  trigger?: HTMLElement;
}

export class EmojiPicker {
  private query = '';
  private category = 'recent';

  constructor(
    private readonly input: HTMLInputElement,
    trigger: HTMLElement,
  ) {
    // The popover attaches itself to the trigger: no reference to keep.
    new Popover({
      trigger,
      width: 'auto',
      build: (close) => this.buildPanel(close),
      onOpen: (panel) => $$<HTMLInputElement>('input[type="search"]', panel)[0]?.focus(),
    });
  }

  private select(emoji: string, close: () => void): void {
    this.input.value = emoji;
    pushRecent(emoji);

    // Notifies the form trackers: without this event, the save bar would not
    // detect the change.
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    close();
  }

  private buildPanel(close: () => void): HTMLElement {
    // A flex child of the panel, allowed to shrink: the panel is the flex
    // container now, and a child that cannot shrink cannot scroll.
    const wrapper = el('div', {
      class: 'flex min-h-0 w-80 max-w-[calc(100vw-2rem)] flex-1 flex-col',
    });

    wrapper.append(
      searchField(ct('searchEmoji', 'Search an emoji…'), (query) => {
        this.query = query;
        renderGrid();
      }),
    );

    // Category tabs
    const tabs = el('div', {
      class: 'border-base-300 flex gap-0.5 overflow-x-auto border-b px-1.5 py-1',
    });

    const recent = readRecent();
    const categories = [
      ...(recent.length > 0 ? [{ key: 'recent', label: ct('recent', 'Recent'), icon: '🕘' }] : []),
      ...EMOJI_CATEGORIES.map((category) => ({
        key: category.key,
        label: ct(category.labelKey, category.label),
        icon: category.icon,
      })),
    ];

    if (!categories.some((c) => c.key === this.category)) {
      this.category = categories[0]?.key ?? 'smileys';
    }

    /*
     * A minimum rather than a fixed height. Fixed at fourteen rem it overflowed
     * a panel shrunk to the room under the trigger, and the clipping happened
     * outside the scrolling element — the same defect the role list had.
     */
    const grid = el('div', { class: 'min-h-0 flex-1 overflow-y-auto p-1.5' });

    const renderTabs = () => {
      tabs.replaceChildren();
      for (const category of categories) {
        const tab = el('button', {
          class:
            'grid size-8 shrink-0 place-items-center rounded-lg text-base transition-colors ' +
            (this.category === category.key ? 'bg-primary/20' : 'hover:bg-base-300'),
          text: category.icon,
          attrs: { type: 'button', title: category.label, 'aria-label': category.label },
        });
        tab.addEventListener('click', (event) => {
          event.stopPropagation();
          this.category = category.key;
          this.query = '';
          renderTabs();
          renderGrid();
        });
        tabs.append(tab);
      }
    };

    const renderGrid = () => {
      grid.replaceChildren();

      // Search wins over category: we look through the whole set.
      const emojis =
        this.query.length > 0
          ? ALL_EMOJI.filter((entry) => entry.keywords.includes(this.query)).map((e) => e.emoji)
          : this.category === 'recent'
            ? readRecent()
            : ALL_EMOJI.filter((entry) => entry.category === this.category).map((e) => e.emoji);

      if (emojis.length === 0) {
        grid.append(
          el('p', {
            class: 'text-base-content/50 px-3 py-8 text-center text-sm',
            text: ct('noEmoji', 'No emoji found'),
          }),
        );
        return;
      }

      const row = el('div', { class: 'grid grid-cols-8 gap-0.5' });
      for (const emoji of emojis) {
        const button = el('button', {
          class:
            'hover:bg-base-300 grid size-8 place-items-center rounded-lg text-lg transition-colors',
          text: emoji,
          attrs: { type: 'button', 'aria-label': emoji },
        });
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.select(emoji, close);
        });
        row.append(button);
      }
      grid.append(row);
    };

    renderTabs();
    renderGrid();
    wrapper.append(tabs, grid);

    // The server's custom emoji, not covered by the Unicode table.
    const footer = el('div', { class: 'border-base-300 border-t p-2' });
    const custom = el('input', {
      class: 'input input-xs input-bordered bg-base-100 w-full font-mono',
      attrs: { type: 'text', placeholder: ct('customEmoji', '<:name:123456789> - server emoji') },
    });
    custom.addEventListener('click', (event) => event.stopPropagation());
    custom.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const value = custom.value.trim();
      if (value) this.select(value, close);
    });
    footer.append(custom);

    const hint = el('p', {
      class: 'text-base-content/40 mt-1.5 text-[0.6875rem] leading-snug',
      text: ct('emojiTip', 'Tip: type \\:name: in Discord to get the code of a custom emoji.'),
    });
    footer.append(hint);

    wrapper.append(footer);
    return wrapper;
  }

  /** Lifts the field out of its original container. */
  static mount(input: HTMLInputElement): EmojiPicker {
    const host = el('div', { class: 'relative flex' });
    input.replaceWith(host);

    input.classList.add('rounded-r-none', 'text-center');
    host.append(input);

    const trigger = el('button', {
      class:
        'border-base-300 bg-base-200 hover:bg-base-300 rounded-r-field grid w-9 shrink-0 ' +
        'place-items-center border border-l-0 text-sm transition-colors',
      text: '😀',
      attrs: {
        type: 'button',
        'aria-label': ct('pickEmoji', 'Pick an emoji'),
        'aria-expanded': 'false',
      },
    });
    host.append(trigger);

    return new EmojiPicker(input, trigger);
  }
}

/** Equips every field marked `data-emoji-input`. */
export function upgradeEmojiInputs(root: ParentNode = document): void {
  for (const input of root.querySelectorAll<HTMLInputElement>('input[data-emoji-input]')) {
    if (input.dataset.emojiReady === 'true') continue;
    input.dataset.emojiReady = 'true';
    EmojiPicker.mount(input);
  }
}
